import { flattenDeck } from "@hali-shares/deck-schema";
import { FindingsList } from "../drawers";
import { isEmptySection, minutesTotal, sectionStatus } from "../model";
import { SlidesGrid } from "../SlidesGrid";
import type { StudioCtx } from "./ctx";

/** Step 4: the talk at a glance, what to fix before the room sees it, every slide, then rehearse or go live. */
export function ReviewStep({ ctx }: { ctx: StudioCtx }) {
  const { deck, settings, findings, deckId } = ctx;
  const slides = flattenDeck(deck).length;
  const minutes = minutesTotal(deck);
  const target = settings.brief.minutes;
  const live = deck.sections.flatMap((s) => s.blocks).filter((b) => b.type === "poll" || b.type === "quiz").length;
  const unreviewed = deck.sections
    .map((s, i) => ({ s, i, status: sectionStatus(s, settings.plan[s.id]) }))
    .filter((x) => x.status !== "reviewed");
  // An empty section's "[To write]" placeholder is listed once, under "Not reviewed yet", not again as a finding.
  const shown = findings.filter(
    (f) => !(f.target.kind === "block" && isEmptySection(deck.sections[f.target.sectionIndex]!)),
  );
  const fix = findings.filter((f) => f.level === "fix").length;

  return (
    <div className="step-page step-page-wide">
      <header className="step-head">
        <p className="step-eyebrow">Step 4 of 4</p>
        <h1 className="step-h">Review and go live</h1>
        <p className="step-lead">Fix what the room would see, look through every slide, then rehearse.</p>
      </header>

      <div className="tiles" aria-label="The talk at a glance">
        <Tile label="Sections" value={String(deck.sections.length)} />
        <Tile label="Slides" value={String(slides)} />
        <Tile
          label="Minutes"
          value={String(minutes)}
          note={target ? (minutes === target ? "matches the slot" : `slot is ${target}`) : undefined}
        />
        <Tile label="Polls and quizzes" value={String(live)} />
        <Tile label="Sections reviewed" value={`${deck.sections.length - unreviewed.length}/${deck.sections.length}`} />
      </div>

      <section className="step-card" aria-labelledby="review-check">
        <h2 id="review-check" className="step-card-h">
          Before you go live
        </h2>
        {(shown.length > 0 || unreviewed.length === 0) && <FindingsList findings={shown} onOpen={ctx.openTarget} />}
        {unreviewed.length > 0 && (
          <div className="finding-group">
            <h3 className="col-label">Not reviewed yet · {unreviewed.length}</h3>
            <ul className="findings">
              {unreviewed.map(({ s, i, status }) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="finding"
                    onClick={() => {
                      ctx.setSelected(i);
                      ctx.goto("slides");
                    }}
                  >
                    <span className="finding-where">Section {i + 1}</span>
                    <span>
                      {s.title}: {status === "empty" ? "no slides yet" : "drafted, not reviewed"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="row-gap">
          <a className="s-btn s-btn-tall" href={`/present/${deckId}?rehearse=1`} target="_blank" rel="noopener">
            Rehearse
          </a>
          <a className="s-btn s-btn-yellow s-btn-tall" href={`/present/${deckId}`} target="_blank" rel="noopener">
            {fix ? "Start live session anyway" : "Start live session"}
          </a>
        </div>
      </section>

      <section className="step-card" aria-labelledby="review-slides">
        <div className="step-card-head">
          <h2 id="review-slides" className="step-card-h">
            Every slide
          </h2>
          <span className="col-num">
            {slides} slides · {minutes} min
          </span>
        </div>
        <SlidesGrid deck={deck} onOpen={ctx.openTarget} />
      </section>
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {note && <span className="tile-note">{note}</span>}
    </div>
  );
}
